import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	Button,
	DynamicIconWrapper,
	Icons,
	Modal,
	SpinnerOverlay } from '@components/common';
import ManageSiteForm from '@components/ModalComponents/ManageSiteForm';

import { useManageSites } from '@hooks/useManageSites';
import { useSiteProfile } from '@hooks/useSiteProfile';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { viewSiteOnMap } from '@slices/map';

import { isEmpty } from '@utils/common';

import { IManageSiteValuesDTO } from '@interfaces/cells/dto/cellsDTO';

import { EVendorsValues } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { routes } from '@constants/routes';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';

import LimenetProfile from './LimenetProfile';
import NokiaProfile from './NokiaProfile';

import styles from './SiteProfile.module.scss';

const SiteProfile = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		accessPermission: { networkOperatorTelecomSitesAttributeUpdater },
	} = useAppSelector(auth);

	const isEditAccess = networkOperatorTelecomSitesAttributeUpdater;

	const [isDirtySiteFormModal, setIsDirtySiteFormModal] = useState(false);
	const [isSiteFormModal, setIsSiteFormModal] = useState(false);

	const closeSiteModal = () => setIsSiteFormModal(false);

	const { profile, isLoadingUpdateProfile } = useSiteProfile({ id });

	const { isLoadingForm, generateSiteId, editSiteHandler } = useManageSites({
		closeSiteModal,
	});

	const toggleSiteModal = () => setIsSiteFormModal((prevState) => !prevState);

	const isNokia = profile?.vendor?.name !== EVendorsValues.limenet;

	const onBackClickHandler = () => navigate(-1);
	const submitHandler = (values: IManageSiteValuesDTO) => editSiteHandler({ values, id: profile.id } as any);

	const handleViewOnMap = async () => {
		await dispatch(viewSiteOnMap(profile.id));

		navigate(routes.map.index, { state: { isRedirectedFromAdminPanel: true } });
	};

	const isViewOnMapAvailable = !isEmpty(profile.technologies);

	return (
		<div className={styles.siteProfileContainer}>
			{isLoadingUpdateProfile && <SpinnerOverlay />}
			<Button
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={styles.whiteWrapper}>
				<div>
					<div>
						<span>Site ID:</span>
						{profile.siteId}
					</div>
					<div>
						<span>Vendor:</span>
						{profile?.vendor?.title}
					</div>
				</div>
				{(profile?.lat || profile?.lng) && (
					<div>
						{profile?.lat && (
							<div>
								<span>Latitude:</span>
								{profile.lat}
							</div>
						)}
						{profile?.lng && (
							<div>
								<span>Longitude:</span>
								{profile?.lng}
							</div>
						)}
					</div>
				)}
				<div>
					<div>
						<span>Site Type/Civil Model:</span>
						{profile?.siteType?.title}
					</div>
					{isViewOnMapAvailable && (
						<div
							className={styles.viewOnMapContainer}
							onClick={handleViewOnMap}
						>
							<span>{viewOnMapTooltip}&nbsp;</span>
							{Icons.locationTransparent()}
						</div>
					)}
				</div>
				<div onClick={isEditAccess ? toggleSiteModal : undefined}>
					<DynamicIconWrapper
						icon={Icons.editIcon}
						isDisabled={!isEditAccess}
					/>
				</div>
			</div>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtySiteFormModal}
				isOpen={isSiteFormModal}
				onClose={closeSiteModal}
				title='Edit site'
				width={MODAL_WIDTH_580}
			>
				<ManageSiteForm
					editableSite={profile}
					generateSiteId={generateSiteId}
					isLinkMode={!!(!isNokia && profile?.crowdCell)}
					isLoadingForm={isLoadingForm}
					setIsDirtyFormModal={setIsDirtySiteFormModal}
					submitHandler={submitHandler}
					toggleSiteModal={toggleSiteModal}
				/>
			</Modal>

			{isNokia
				? <NokiaProfile />
				: <LimenetProfile
					isEditAccess={isEditAccess}
					profile={profile}
				/>
			}
		</div>
	);
};

export default SiteProfile;
